import {
  CreatePromoCodeRequest,
  PromoCode,
  PromoCodeList,
  UpdatePromoCodeRequest,
} from '../stores/types/promoCode.interface';
import { API_KEYS, Api } from './base.api';

export interface FetchPromoCodesParams {
  companyId: string;
  limit?: number;
  offset?: number;
  'filter_eq[promotionId]'?: string;
  'filter_like[code]'?: string;
}

export const promoCodeApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    fetchPromoCodes: builder.query<PromoCodeList, FetchPromoCodesParams>({
      query: (params) => ({
        url: `/internal/core/v2/${API_KEYS.PROMO_CODES}`,
        method: 'GET',
        params,
      }),
      providesTags: () => [{ type: API_KEYS.PROMO_CODES, id: 'LIST' }],
    }),
    createPromoCode: builder.mutation<
      PromoCode,
      { companyId: string; payload: CreatePromoCodeRequest }
    >({
      query: ({ companyId, payload }) => ({
        url: `/internal/core/v2/${API_KEYS.PROMO_CODES}`,
        method: 'POST',
        params: { companyId },
        body: payload,
        toast: {
          successTrans: 'promo_code_create_success_toast',
          failedTrans: 'promo_code_create_failed_toast',
          errorMessageMap: {
            'Code is in use': 'promo_code_duplicate_error_toast',
          },
        },
      }),
      invalidatesTags: () => [{ type: API_KEYS.PROMO_CODES, id: 'LIST' }],
    }),
    // Partial update — only DRAFT codes are editable (the BE no-ops other statuses).
    updatePromoCode: builder.mutation<
      PromoCode,
      { promoCodeId: string; companyId: string; payload: UpdatePromoCodeRequest }
    >({
      query: ({ promoCodeId, companyId, payload }) => ({
        url: `/internal/core/v2/${API_KEYS.PROMO_CODES}/${promoCodeId}`,
        method: 'PATCH',
        params: { companyId },
        body: payload,
        toast: {
          successTrans: 'promo_code_update_success_toast',
          failedTrans: 'promo_code_update_failed_toast',
          errorMessageMap: {
            'Code is in use': 'promo_code_duplicate_error_toast',
          },
        },
      }),
      invalidatesTags: () => [{ type: API_KEYS.PROMO_CODES, id: 'LIST' }],
    }),
    // Soft-deletes published codes (archive) and hard-deletes DRAFT ones; the BE decides
    // based on status. Used for both the "Delete" (draft) and "Archive" (published) actions.
    // `isArchive` only selects the toast wording so archiving never says "delete".
    deletePromoCode: builder.mutation<
      void,
      { promoCodeId: string; companyId: string; isArchive?: boolean }
    >({
      query: ({ promoCodeId, companyId, isArchive }) => ({
        url: `/internal/core/v2/${API_KEYS.PROMO_CODES}/${promoCodeId}`,
        method: 'DELETE',
        params: { companyId },
        toast: {
          successTrans: isArchive
            ? 'promo_code_archive_success_toast'
            : 'promo_code_delete_success_toast',
          failedTrans: isArchive
            ? 'promo_code_archive_failed_toast'
            : 'promo_code_delete_failed_toast',
        },
      }),
      invalidatesTags: () => [{ type: API_KEYS.PROMO_CODES, id: 'LIST' }],
    }),
    // Publishes a single DRAFT code. Returns the promoCodeId (UUID). Also bumps the parent
    // promotion's promoCodeCount, so we invalidate the promotion entity too.
    publishPromoCode: builder.mutation<
      string,
      { promoCodeId: string; companyId: string; promotionId: string }
    >({
      query: ({ promoCodeId, companyId }) => ({
        url: `/internal/core/v2/${API_KEYS.PROMO_CODES}/${promoCodeId}/publish`,
        method: 'POST',
        params: { companyId },
        toast: {
          successTrans: 'promo_code_publish_success_toast',
          failedTrans: 'promo_code_publish_failed_toast',
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: API_KEYS.PROMO_CODES, id: 'LIST' },
        { type: API_KEYS.PROMOTIONS, id: arg.promotionId },
      ],
    }),
    pausePromoCode: builder.mutation<PromoCode, { promoCodeId: string; companyId: string }>({
      query: ({ promoCodeId, companyId }) => ({
        url: `/internal/core/v2/${API_KEYS.PROMO_CODES}/${promoCodeId}/pause`,
        method: 'POST',
        params: { companyId },
        toast: {
          successTrans: 'promo_code_pause_success_toast',
          failedTrans: 'promo_code_pause_failed_toast',
        },
      }),
      invalidatesTags: () => [{ type: API_KEYS.PROMO_CODES, id: 'LIST' }],
    }),
    resumePromoCode: builder.mutation<PromoCode, { promoCodeId: string; companyId: string }>({
      query: ({ promoCodeId, companyId }) => ({
        url: `/internal/core/v2/${API_KEYS.PROMO_CODES}/${promoCodeId}/resume`,
        method: 'POST',
        params: { companyId },
        toast: {
          successTrans: 'promo_code_resume_success_toast',
          failedTrans: 'promo_code_resume_failed_toast',
        },
      }),
      invalidatesTags: () => [{ type: API_KEYS.PROMO_CODES, id: 'LIST' }],
    }),
  }),
});

export const {
  useFetchPromoCodesQuery,
  useCreatePromoCodeMutation,
  useUpdatePromoCodeMutation,
  useDeletePromoCodeMutation,
  usePublishPromoCodeMutation,
  usePausePromoCodeMutation,
  useResumePromoCodeMutation,
} = promoCodeApi;
